import { inject, injectable } from 'inversify'
import * as THREE from 'three'
import { TYPES } from '@/plugins/cad/module/container/types'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import type { CADViewManager } from '../manager/CADViewManager'
import type { MouseTipHelper } from './MouseTipHelper'

/**
 * 极轴吸附辅助器，按给定角度集合将当前点吸附到最近的极角方向。
 */
@injectable()
export class PolarHelper {
  @inject(TYPES.CADViewManager) private readonly viewManager!: CADViewManager
  @inject(TYPES.MouseTipHelper) private readonly mouseTipHelper!: MouseTipHelper

  private enabled: boolean = false
  /** 当前启用的极轴角度集合，单位度 */
  public polarAngles: number[] = []

  private polarLineObj: THREE.Line | null = null

  /**
   * 查询极轴开关状态。
   *
   * @returns 是否已启用
   */
  public isEnabled() {
    return this.enabled
  }

  /**
   * 设置极轴角度集合；集合为空时自动关闭。
   *
   * @param angles - 极轴角度列表，单位度
   */
  public setPolarAngles(angles: number[]) {
    this.polarAngles = angles
    this.setEnabled(angles.length > 0)
  }

  /**
   * 设置极轴启用状态。
   *
   * @param val - 是否启用极轴
   */
  public setEnabled(val: boolean) {
    if (this.enabled === val) {
      return
    }
    this.enabled = val

    if (!this.enabled) {
      this.clearUI()
    }
  }

  /**
   * 根据极轴吸附结果更新鼠标提示 UI。
   *
   * @param screenPos - 指针屏幕坐标
   * @param polarRes - 极轴吸附结果，缺省时移除提示
   */
  public updateUI(_screenPos: { x: number; y: number }, polarRes: any) {
    if (!this.enabled) {
      return
    }

    if (polarRes) {
      this.mouseTipHelper.addTip('Polar_Tip', CadTipKey.auxiliary.polar.status, {
        distance: polarRes.distance.toFixed(4),
        angle: polarRes.angle,
      })

      // 给极轴 tip 加左边框分隔线
      const el = this.mouseTipHelper.getTip('Polar_Tip')
      if (el) {
        el.style.borderLeft = '1px solid #999'
        el.style.marginLeft = '6px'
        el.style.paddingLeft = '6px'
      }
    } else {
      this.mouseTipHelper.removeTip('Polar_Tip')
    }
  }

  /** 清理极轴辅助线与提示 */
  public clearUI() {
    this.hidePolarLine()
    this.mouseTipHelper.removeTip('Polar_Tip')
  }

  /**
   * 计算当前点相对起点的极轴吸附结果。
   *
   * @param worldPos - 当前世界坐标
   * @param option - 吸附参数，包含参考起点
   * @returns 吸附后的点、距离与角度；未命中阈值返回 null
   */
  public calculatePolarSnap(worldPos: any, option?: { startPoint?: any }) {
    if (!this.enabled || !option?.startPoint || this.polarAngles.length === 0) {
      this.hidePolarLine()
      return null
    }

    const startPoint = option.startPoint
    const dx = worldPos.x - startPoint.x
    const dy = worldPos.y - startPoint.y
    const distance = Math.sqrt(dx * dx + dy * dy)
    const angle = Math.atan2(dy, dx) * (180 / Math.PI)

    let snapAngle: number | null = null
    let minDiff = 6

    for (const pAngle of this.polarAngles) {
      const snapCandidate = Math.round(angle / pAngle) * pAngle
      const diff = Math.abs(angle - snapCandidate)
      if (diff <= 5 && diff < minDiff) {
        minDiff = diff
        snapAngle = snapCandidate
      }
    }

    if (snapAngle !== null) {
      const radian = snapAngle * (Math.PI / 180)
      this.drawPolarLine(startPoint, snapAngle)
      return {
        x: startPoint.x + distance * Math.cos(radian),
        y: startPoint.y + distance * Math.sin(radian),
        z: startPoint.z || worldPos.z || 0,
        distance,
        angle: snapAngle,
      }
    }

    this.hidePolarLine()
    return null
  }

  /**
   * 绘制极轴参考线。
   *
   * @param startPoint - 起点世界坐标
   * @param angle - 极轴角度，单位度
   */
  public drawPolarLine(startPoint: any, angle: number) {
    if (!this.polarLineObj) {
      const mat = new THREE.LineDashedMaterial({
        color: 0x118af0,
        dashSize: 0.5,
        gapSize: 0.5,
        depthTest: false,
      })
      this.polarLineObj = new THREE.Line(new THREE.BufferGeometry(), mat)
      this.viewManager.attachTempObject(this.polarLineObj)
    }
    const radian = angle * (Math.PI / 180)
    const length = 1000
    const endPoint = {
      x: startPoint.x + length * Math.cos(radian),
      y: startPoint.y + length * Math.sin(radian),
      z: startPoint.z || 0,
    }
    this.polarLineObj.geometry.setFromPoints([
      new THREE.Vector3(startPoint.x, startPoint.y, startPoint.z || 0),
      new THREE.Vector3(endPoint.x, endPoint.y, endPoint.z || 0),
    ])
    this.polarLineObj.computeLineDistances()
    this.polarLineObj.visible = true
  }

  /** 隐藏当前极轴参考线 */
  public hidePolarLine() {
    if (this.polarLineObj) {
      this.polarLineObj.visible = false
    }
  }
}
